import PlatformLinks from "@/components/PlatformLinks";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { MarketplaceGrid } from "@/components/MarketplaceLogos";
import { PREMIUM_MARKETPLACES } from "@/data/content";
import { CheckCircle2, AlertTriangle, Sparkles, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

const CHALLENGES = [
  { icon: AlertTriangle, title: "Brand approval", desc: "Premium platforms approve only registered, established brands. Most applications are rejected silently." },
  { icon: AlertTriangle, title: "Compliance", desc: "Strict catalog quality, returns thresholds and branding standards. One slip and listings go down." },
  { icon: AlertTriangle, title: "High-quality catalogs", desc: "Studio-grade photography, lookbook content, and styled lifestyle imagery are table stakes." },
];

const SOLUTIONS = [
  { icon: Sparkles, title: "Brand onboarding", desc: "We prepare your brand kit, dossiers, and authorised seller documentation." },
  { icon: Sparkles, title: "Approval support", desc: "Direct relationships with category managers across Nykaa, Myntra and Tata Cliq." },
  { icon: Sparkles, title: "Premium listing optimization", desc: "Studio-grade catalog production, lookbooks, and editorial copy." },
  { icon: Sparkles, title: "Ad strategy", desc: "Premium ad placements built for brand-conscious audiences with high AOV." },
  { icon: Sparkles, title: "Growth management", desc: "Ongoing portfolio management to scale GMV without diluting brand." },
];

export default function PremiumMarketplaces() {
  return (
    <>
      <SEO
        title="Premium Marketplaces — Nykaa, Myntra, Ajio Luxe & Tata Cliq"
        description="Premium marketplace onboarding for Nykaa, Myntra, Ajio Luxe and Tata Cliq. Brand approval support, premium catalog production, and growth management."
        path="/premium-marketplaces"
      />

      <section className="border-b-2 border-black bg-black py-20 text-white md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">Premium Marketplaces</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            Build a Strong<br /><span className="text-[#FFD700]">Brand Presence.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-white/70 md:text-lg">
            Nykaa, Myntra, Ajio Luxe and Tata Cliq are curated platforms with strict entry. Brand positioning here pays compounding dividends.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">The Premium Channels</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">Four marketplaces. Four playbooks.</h2>
          <div className="mt-10">
            <MarketplaceGrid items={PREMIUM_MARKETPLACES} />
          </div>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 md:grid-cols-2 md:px-8">
          <div>
            <div className="eyebrow text-black/60">The Challenges</div>
            <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">Why premium is hard.</h2>
            <div className="mt-8 space-y-4">
              {CHALLENGES.map((c) => (
                <div key={c.title} className="card-brutal flex gap-4 p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-[#FFD700]">
                    <c.icon size={18} />
                  </div>
                  <div>
                    <h3 className="display text-lg font-black">{c.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-black/70">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="eyebrow text-[#a08800]">The Orina Solution</div>
            <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">How we fix it.</h2>
            <div className="mt-8 space-y-4">
              {SOLUTIONS.map((s) => (
                <div key={s.title} className="card-brutal flex gap-4 !bg-[#FFD700] p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-black bg-black text-[#FFD700]">
                    <CheckCircle2 size={18} />
                  </div>
                  <div>
                    <h3 className="display text-lg font-black">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-black/80">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PlatformLinks groups={["premium"]} title="Premium marketplace services" />

      <CtaBanner
        title="Ready for premium positioning?"
        subtitle="Apply for Nykaa, Myntra, Ajio Luxe or Tata Cliq with a partner who has direct category relationships."
      />
    </>
  );
}
